import {ssrSafeLocation} from '@github-ui/ssr-utils'
import type {UnifiedBatchItem} from '../stores/slices/unified-batch'

/** True only for a same-origin http(s) URL, resolved against the current origin. */
export function isSafeSourceUri(value: string | undefined): boolean {
  if (typeof value !== 'string') return false
  try {
    const url = new URL(value, ssrSafeLocation.origin)
    return (url.protocol === 'https:' || url.protocol === 'http:') && url.origin === ssrSafeLocation.origin
  } catch {
    return false
  }
}

/**
 * Keeps `value` only if it parses as an http(s) URL, else `undefined`. Unlike
 * `isSafeSourceUri` it allows cross-origin hosts, since avatars are served off-origin
 * (e.g. `avatars.githubusercontent.com`) and profile links may point at an enterprise
 * host; the goal is only to block non-http schemes (e.g. a planted `javascript:` that
 * would flow into an `href`) and values that make `new URL` throw, not same-origin.
 */
export function safeHttpUrl(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined
  try {
    const url = new URL(value, ssrSafeLocation.origin)
    return url.protocol === 'https:' || url.protocol === 'http:' ? value : undefined
  } catch {
    return undefined
  }
}

/**
 * Drops any untrusted URL field on an item that isn't a safe http(s) value:
 * `sourceUri` (flows into `window.location.href` for outdated rows), and the
 * author's `avatarUrl`/`url` (flow into `GitHubAvatar`'s `new URL(src)` and the
 * author link's `href`). Applied at store ingress so freshly added rows share the
 * same URL contract as rows rehydrated from sessionStorage, rather than only
 * guarding the recovered ones.
 */
export function normalizeUnifiedBatchItemUrls(item: UnifiedBatchItem): UnifiedBatchItem {
  return {
    ...item,
    sourceUri: isSafeSourceUri(item.sourceUri) ? item.sourceUri : undefined,
    author: {
      ...item.author,
      avatarUrl: safeHttpUrl(item.author.avatarUrl),
      url: safeHttpUrl(item.author.url),
    },
  }
}
